// ── Broken Hearts: a rose garden at golden hour. Light, warm and romantic —
//    trellis arches on the horizon, drifting petals, bokeh and soft hearts. ──

import { useMemo, useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { useParallax, useAmbientBeat, usePrefersReducedMotion, seeded } from '../../lib/battle3d';

// A row of rose arches receding toward the horizon
const Trellis = ({ tone, count = 7 }) => (
  <svg viewBox="0 0 1000 180" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
    {Array.from({ length: count }, (_, i) => {
      const x = (i / (count - 1)) * 1000;
      return (
        <g key={i}>
          {/* montanti e arco */}
          <path d={`M${x - 40} 180 L${x - 40} 84 Q${x} 20 ${x + 40} 84 L${x + 40} 180`}
            fill="none" stroke={tone} strokeWidth="5" strokeLinecap="round" opacity="0.9" />
          {/* traversa */}
          <line x1={x - 40} y1="126" x2={x + 40} y2="126" stroke={tone} strokeWidth="3" opacity="0.5" />
          {/* rose rampicanti lungo la curva */}
          {[-38, -26, -12, 0, 12, 26, 38].map((dx, j) => {
            const t = dx / 40;
            const y = 84 - 58 * (1 - t * t);
            return <circle key={j} cx={x + dx} cy={y} r={j % 2 ? 4.5 : 6.5} fill={tone} opacity="0.8" />;
          })}
          {/* fogliame ai piedi */}
          <ellipse cx={x - 40} cy={172} rx="17" ry="10" fill={tone} opacity="0.55" />
          <ellipse cx={x + 40} cy={172} rx="17" ry="10" fill={tone} opacity="0.55" />
        </g>
      );
    })}
  </svg>
);

// Low hedge line
const Hedge = ({ tone }) => (
  <svg viewBox="0 0 1000 90" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
    <path
      d="M0 90 L0 52 Q40 28 84 48 Q126 22 170 46 Q214 24 258 48 Q300 26 344 46 Q390 22 434 48 Q478 26 522 46 Q566 22 610 48 Q654 26 698 46 Q742 22 786 48 Q830 26 874 46 Q918 24 962 48 Q984 34 1000 50 L1000 90 Z"
      fill={tone}
    />
  </svg>
);

const Petal = ({ p, tone }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{
      y: ['-10vh', '108vh'],
      x: [0, p.drift, p.drift * 0.4, p.drift * 1.2],
      rotate: [0, 220 * p.spin, 420 * p.spin],
      opacity: [0, 0.9, 0.8, 0],
    }}
    transition={{ duration: p.dur, repeat: Infinity, delay: p.delay, ease: 'linear' }}
    style={{ position: 'absolute', left: `${p.x}%`, top: 0, width: p.size, height: p.size * 0.72, pointerEvents: 'none' }}
  >
    <svg viewBox="0 0 24 18" style={{ width: '100%', height: '100%' }}>
      <path d="M1 9 Q6 0 14 1 Q23 3 23 9 Q23 15 14 17 Q6 18 1 9 Z" fill={tone} opacity="0.85" />
      <path d="M4 9 Q9 4 15 5" stroke="rgba(255,255,255,0.65)" strokeWidth="1" fill="none" />
    </svg>
  </motion.div>
);

const RoseGarden = ({ mobile = false, dimmed = false }) => {
  const reduced = usePrefersReducedMotion();
  const { px, py } = useParallax(!reduced);
  const [pulse, setPulse] = useState(0);

  useAmbientBeat(() => setPulse(Date.now()), { min: 8000, max: 15000, enabled: !reduced });

  const skyX = useTransform(px, (v) => v * -7);
  const skyY = useTransform(py, (v) => v * -3);
  const farX = useTransform(px, (v) => v * -22);
  const farY = useTransform(py, (v) => v * -9);
  const midX = useTransform(px, (v) => v * -40);
  const midY = useTransform(py, (v) => v * -17);
  const nearX = useTransform(px, (v) => v * -70);
  const nearY = useTransform(py, (v) => v * -30);

  const petals = useMemo(() => {
    const n = reduced ? 0 : mobile ? 14 : 30;
    const rnd = seeded(1402);
    return Array.from({ length: n }, () => ({
      x: rnd() * 100, size: 10 + rnd() * 16, dur: 9 + rnd() * 10,
      delay: rnd() * 12, drift: (rnd() - 0.5) * 220, spin: rnd() > 0.5 ? 1 : -1,
    }));
  }, [mobile, reduced]);

  const bokeh = useMemo(() => {
    const rnd = seeded(707);
    return Array.from({ length: mobile ? 7 : 13 }, () => ({
      x: rnd() * 100, y: rnd() * 62, size: 30 + rnd() * 90, dur: 9 + rnd() * 9, delay: rnd() * 6,
    }));
  }, [mobile]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      {/* Golden-hour sky */}
      <motion.div style={{
        position: 'absolute', inset: '-6%', x: skyX, y: skyY,
        background: `radial-gradient(ellipse 90% 60% at 50% 88%, rgba(255,214,232,0.95) 0%, transparent 70%),
                     linear-gradient(to bottom, #fff7fb 0%, #ffe9f3 42%, #ffd9e8 72%, #ffc9dd 100%)`,
      }} />

      {/* Low sun */}
      <motion.div
        animate={reduced ? {} : { opacity: [0.55, 0.8, 0.55] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', left: '50%', bottom: '46%', width: mobile ? 220 : 380, height: mobile ? 220 : 380,
          marginLeft: mobile ? -110 : -190, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,214,232,0.7) 38%, transparent 70%)',
          filter: 'blur(6px)',
        }}
      />

      {/* Bokeh */}
      {!reduced && bokeh.map((b, i) => (
        <motion.div key={i}
          animate={{ y: [0, -22, 0], opacity: [0.12, 0.3, 0.12] }}
          transition={{ duration: b.dur, repeat: Infinity, delay: b.delay, ease: 'easeInOut' }}
          style={{
            position: 'absolute', left: `${b.x}%`, top: `${b.y}%`, width: b.size, height: b.size,
            borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,190,220,0.4) 55%, transparent 72%)',
            filter: 'blur(3px)',
          }} />
      ))}

      {/* Far trellis */}
      <motion.div style={{ position: 'absolute', left: '-8%', right: '-8%', bottom: '57%', height: mobile ? '12%' : '17%', x: farX, y: farY, opacity: 0.35 }}>
        <Trellis tone="#f0a8c6" count={mobile ? 5 : 8} />
      </motion.div>

      {/* Near trellis */}
      <motion.div style={{ position: 'absolute', left: '-12%', right: '-12%', bottom: '50%', height: mobile ? '16%' : '23%', x: midX, y: midY, opacity: 0.62 }}>
        <Trellis tone="#db6f9c" count={mobile ? 4 : 6} />
      </motion.div>

      {/* Hedge */}
      <motion.div style={{ position: 'absolute', left: '-10%', right: '-10%', bottom: '44%', height: mobile ? '9%' : '13%', x: midX, y: midY }}>
        <Hedge tone="#c25f8c" />
      </motion.div>

      {/* Garden path receding */}
      <motion.div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '47%', overflow: 'hidden', x: nearX, y: nearY }}>
        <div style={{
          position: 'absolute', left: '-25%', right: '-25%', top: 0, height: '190%',
          transform: 'perspective(760px) rotateX(62deg)', transformOrigin: 'center top',
          backgroundImage: `
            repeating-linear-gradient(to bottom, rgba(190,24,93,0.10) 0 1px, transparent 1px 42px),
            repeating-linear-gradient(to right, rgba(190,24,93,0.07) 0 1px, transparent 1px 62px),
            radial-gradient(ellipse 50% 55% at 50% 0%, rgba(255,255,255,0.75) 0%, transparent 62%),
            linear-gradient(to bottom, #ffe4ef 0%, #ffd2e3 60%, #ffc2d9 100%)`,
        }} />
      </motion.div>

      {/* Heartbeat wash */}
      <motion.div key={pulse} initial={{ opacity: 0 }}
        animate={pulse ? { opacity: [0, 0.3, 0.05, 0.18, 0] } : {}}
        transition={{ duration: 1.2, times: [0, 0.12, 0.4, 0.55, 1] }}
        style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 60%, rgba(255,120,175,0.55), transparent 62%)' }} />

      {/* Falling petals */}
      {petals.map((p, i) => <Petal key={i} p={p} tone={i % 3 === 0 ? '#f9a8d4' : i % 3 === 1 ? '#f472b6' : '#fbcfe8'} />)}

      {/* Drifting hearts */}
      {!reduced && Array.from({ length: mobile ? 4 : 7 }, (_, i) => (
        <motion.div key={`h${i}`}
          animate={{ y: ['8vh', '-72vh'], opacity: [0, 0.5, 0.35, 0], scale: [0.7, 1, 0.85] }}
          transition={{ duration: 13 + i * 2.5, repeat: Infinity, delay: i * 2.6, ease: 'linear' }}
          style={{ position: 'absolute', left: `${6 + i * 13}%`, bottom: 0, fontSize: 14 + (i % 3) * 8, filter: 'drop-shadow(0 2px 6px rgba(236,72,153,0.4))' }}
        >
          ❤
        </motion.div>
      ))}

      {/* Soft rose vignette — the light must stay light */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, transparent 46%, rgba(190,24,93,0.13) 80%, rgba(157,23,77,0.26) 100%)' }} />
      {dimmed && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,240,246,0.5)' }} />}
    </div>
  );
};

export default RoseGarden;
